import './activity-card.scss';
import {useState, useEffect, useContext} from 'react';
import {Tooltip, Box, Typography, Avatar, Chip, useTheme, IconButton} from '@mui/material';
import {
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineDot,
  TimelineContent,
} from '@mui/lab';
import {useTranslation} from 'react-i18next';
import {ActivityEndType} from '../../backend/enums/activity-end-type';
import {CameraFacing} from '../../backend/enums/camera-facing';
import {DeviceType} from '../../backend/enums/device-type';
import {ReminderResponseValue} from '../../backend/enums/reminder-response-value';
import {ActivityInfo} from '../../backend/interfaces/activity-info';
import {Application} from '../../backend/interfaces/application';
import {Reminder} from '../../backend/interfaces/reminder';
import {ActivityTypeDataType} from '../../backend/enums/activity-type-data-type';
import {CaregiverMode} from '../../enums/caregiver-mode';
import {translations} from '../../i18n/translations';
import {ApplicationsRepository} from '../../repositories/applications';
import {RemindersRepository} from '../../repositories/reminders';
import {UsersRepository} from '../../repositories/users';
import {hoursToHoursAndMinutes} from '../../utils/functions/hours-to-hours-and-minutes';
import {AuxiliaryDataCacheContextType} from '../../contexts/auxiliary-data-cache/types';
import {AuxiliaryDataCacheContext} from '../../contexts/auxiliary-data-cache/context';
import {DataCacheManager} from '../../managers/data-cache';
import {CallStartedActivityData} from '../../backend/interfaces/call-started-activity-data';
import {DrawingSentActivityData} from '../../backend/interfaces/drawing-sent-activity-data';
import {AuthContextType} from '../../contexts/auth/types';
import {VideoMessageActivityData} from '../../backend/interfaces/video-message-activity-data';
import {PhotoSentActivityData} from '../../backend/interfaces/photo-sent-activity-data';
import {AuthContext} from '../../contexts/auth/context';
import {ApplicationProgressActivityData} from '../../backend/interfaces/application-progress-activity-data';
import {ApplicationBattleActivityData} from '../../backend/interfaces/application-battle-activity-data';
import {ReminderResponseActivityData} from '../../backend/interfaces/reminder-response-activity-data';
import {CallMissedActivityData} from '../../backend/interfaces/call-missed-activity-data';
import {CallRejectedActivityData} from '../../backend/interfaces/call-rejected-activity-data';
import {NewRelationshipActivityData} from '../../backend/interfaces/new-relationship-activity-data';
import {avatarSizesAndCoefficients} from '../../utils/constants/avatar-sizes-and-coefficients';
import {UserPublicInfo} from '../../backend/responses/user-public-info';
import {DrawingSavedActivityData} from '../../backend/interfaces/drawing-saved-activity-data';
import {PhotoSavedActivityData} from '../../backend/interfaces/photo-saved-activity-data';
import {VideoSentActivityData} from '../../backend/interfaces/video-sent-activity-data';
import {VideoSavedActivityData} from '../../backend/interfaces/video-saved-activity-data';
import {isSvg} from '../../utils/functions/validate-uri-svg';
import moment from 'moment';
import DesktopIcon from '@mui/icons-material/DesktopWindows';
import TabletIcon from '@mui/icons-material/Tablet';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PersonIcon from '@mui/icons-material/Person';
import AppsIcon from '@mui/icons-material/Apps';
import CameraFrontIcon from '@mui/icons-material/CameraFront';
import CameraRearIcon from '@mui/icons-material/CameraRear';
import PhotoCameraFrontIcon from '@mui/icons-material/PhotoCameraFront';
import PhotoCameraBackIcon from '@mui/icons-material/PhotoCameraBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import {PhotoSharedActivityData} from '../../backend/interfaces/photo-shared-activity-data';
import {VideoSharedActivityData} from '../../backend/interfaces/video-shared-activity-data';
import {DrawingSharedActivityData} from '../../backend/interfaces/drawing-shared-activity-data';

const ApplicationChip = (props: {value: Application}) => {
  // Translations
  const {t} = useTranslation();

  return (
    <Tooltip title={t(translations.activity_card.application) as string}>
      <Box className="activity-card-application-chip-container">
        <Chip
          avatar={
            <Avatar
              alt={props.value.name}
              src={props.value.picture || props.value.iconUrl || ''}
              sx={{
                width: `${avatarSizesAndCoefficients.avatarSizes.tiny}px`,
                height: `${avatarSizesAndCoefficients.avatarSizes.tiny}px`,
              }}
            >
              <AppsIcon
                sx={{
                  width: `${
                    avatarSizesAndCoefficients.avatarSizes.tiny *
                    avatarSizesAndCoefficients.iconSizeCoefficient
                  }px`,
                  height: `${
                    avatarSizesAndCoefficients.avatarSizes.tiny *
                    avatarSizesAndCoefficients.iconSizeCoefficient
                  }px`,
                }}
              />
            </Avatar>
          }
          label={props.value.name}
          variant="filled"
        />
      </Box>
    </Tooltip>
  );
};

const UserChip = (props: {value: UserPublicInfo}) => (
  <Box className="activity-card-user-chip-container">
    <Chip
      avatar={
        <Avatar
          alt={props.value.name}
          src={props.value.picture}
          sx={{
            width: `${avatarSizesAndCoefficients.avatarSizes.tiny}px`,
            height: `${avatarSizesAndCoefficients.avatarSizes.tiny}px`,
          }}
        >
          <PersonIcon
            sx={{
              width: `${
                avatarSizesAndCoefficients.avatarSizes.tiny *
                avatarSizesAndCoefficients.iconSizeCoefficient
              }px`,
              height: `${
                avatarSizesAndCoefficients.avatarSizes.tiny *
                avatarSizesAndCoefficients.iconSizeCoefficient
              }px`,
            }}
          />
        </Avatar>
      }
      label={props.value.name}
      variant="filled"
    />
  </Box>
);

const ParticipantsChips = (props: {activityId?: string; value: Array<UserPublicInfo>}) => {
  // Translations
  const {t} = useTranslation();

  // Default result
  return (
    <Tooltip title={t(translations.activity_card.participants) as string}>
      <Box className="activity-card-participants-chip-container">
        {props.value.map((participant, index) => {
          return (
            <Box
              key={`participant-${participant.id}-activity-${props.activityId}`}
              className="activity-card-participant-container"
            >
              {props.value.length > 1 && index === props.value.length - 1 && (
                <Typography className="activity-card-participant-and-text" variant="body2">
                  {t(translations.activity_card.and) as string}
                </Typography>
              )}
              <UserChip value={participant} />
              {props.value.length > 1 && index < props.value.length - 2 && (
                <Typography className="activity-card-participant-comma-text" variant="body2">
                  ,
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Tooltip>
  );
};

const CameraFacingChip = (props: {value: CameraFacing}) => {
  // Translations
  const {t} = useTranslation();

  return (
    <Chip
      avatar={
        <Box className="activity-card-camera-facing-chip-container">
          {props.value === CameraFacing.Front && (
            <CameraFrontIcon className="activity-card-camera-facing-chip-avatar-icon" />
          )}
          {props.value === CameraFacing.Rear && (
            <CameraRearIcon className="activity-card-camera-facing-chip-avatar-icon" />
          )}
          {props.value === CameraFacing.External && (
            <PhotoCameraFrontIcon className="activity-card-camera-facing-chip-avatar-icon" />
          )}
          {props.value === CameraFacing.Unknown && (
            <PhotoCameraBackIcon className="activity-card-camera-facing-chip-avatar-icon" />
          )}
        </Box>
      }
      label={t(translations.camera_facing[props.value])}
      variant="filled"
    />
  );
};

const ReminderResponseValueChip = (props: {value: ReminderResponseValue}) => {
  // Translations
  const {t} = useTranslation();

  return (
    <Chip
      avatar={
        <Box className="activity-card-reminder-response-chip-container">
          {props.value === ReminderResponseValue.Positive && (
            <CheckIcon
              className="activity-card-reminder-response-chip-avatar-icon"
              color="success"
            />
          )}
          {props.value === ReminderResponseValue.Negative && (
            <CloseIcon className="activity-card-reminder-response-chip-avatar-icon" color="error" />
          )}
          {props.value === ReminderResponseValue.NotResponding && (
            <CheckBoxOutlineBlankIcon
              className="activity-card-reminder-response-chip-avatar-icon"
              color="warning"
            />
          )}
        </Box>
      }
      label={t(translations.reminder_response_values[props.value])}
      variant="filled"
    />
  );
};

const ReminderChip = (props: {value: Reminder}) => (
  <Box className="activity-card-reminder-chip-container">
    <Typography
      className="activity-card-reminder-chip-title"
      variant="body2"
      fontStyle="bold"
    >{`"${props.value.title}"`}</Typography>
    <Typography variant="caption">{`"${props.value.message}"`}</Typography>
  </Box>
);

/**
 * Activity card used on the activity timeline component.
 *
 * Note: We keep the data cache inside a singleton and not in context because this is
 * the faster way. The context takes "too much" time so requests can be repeated and
 * the context can be out of sync when too much state changes are done at the same time.
 * However, we also keep the cache inside a context because we need to try to get the activity
 * data again when new values are added to the cache. Using class variables inside useEffect
 * is not possible to achieve this so we had to create this auxiliary context for this.
 */
export const ActivityCard = (props: {caregiverMode: CaregiverMode; value: ActivityInfo}) => {
  // Get context
  const auxiliaryDataCacheContext: AuxiliaryDataCacheContextType =
    useContext(AuxiliaryDataCacheContext);
  const authContext: AuthContextType = useContext(AuthContext);

  // Hold state
  const [participants, setParticipants] = useState<Array<UserPublicInfo>>([]);
  const [application, setApplication] = useState<Application | undefined>();
  const [reminder, setReminder] = useState<Reminder | undefined>();
  const [errorOnImage, setErrorOnImage] = useState<boolean>(false);
  const [errorOnVideo, setErrorOnVideo] = useState<boolean>(false);
  const [showDescription, setShowDescription] = useState<boolean>(false);

  // Translations
  const {t} = useTranslation();

  // Get theme
  const theme = useTheme();

  // Get activity data
  useEffect(() => {
    const getActivityData = async () => {
      // Load application
      setApplicationOnActivityCard();

      // Load participants
      setParticipantsOnActivityCard();

      // Load reminder
      setReminderOnActivityCard();
    };

    // Load data for the activity
    getActivityData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auxiliaryDataCacheContext.state]);

  const setApplicationOnActivityCard = async () => {
    if (
      (props.value.activityType.dataType === ActivityTypeDataType.ApplicationBattle ||
        props.value.activityType.dataType === ActivityTypeDataType.ApplicationProgress) &&
      !application
    ) {
      let applicationId: string | undefined;
      if (
        props.value.activityType.dataType === ActivityTypeDataType.ApplicationBattle &&
        props.value.activity.data
      ) {
        applicationId = (props.value.activity.data as ApplicationBattleActivityData).applicationId;
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.ApplicationProgress &&
        props.value.activity.data
      ) {
        applicationId = (props.value.activity.data as ApplicationProgressActivityData)
          .applicationId;
      }

      // Make sure the application id is set
      if (applicationId) {
        // Make sure an error did not occur previously with the application
        if (!DataCacheManager.instance.applicationsWithError[applicationId]) {
          if (!DataCacheManager.instance.pendingApplications[applicationId]) {
            if (!DataCacheManager.instance.applications[applicationId]) {
              let updateContext: boolean = false;

              try {
                // Application is being fetched
                DataCacheManager.instance.addPendingApplication(applicationId);

                // Get
                const result: Application = await ApplicationsRepository.instance.getById(
                  applicationId,
                  true
                );

                if (result) {
                  // Save it on cache
                  DataCacheManager.instance.addApplication(applicationId, result);

                  // Update
                  updateContext = true;

                  // Set it
                  setApplication(result);
                } else {
                  // Error
                  throw new Error('Empty application.');
                }
              } catch (error) {
                // Log
                console.debug(`🔴 Could not get application ${applicationId}.`, error);

                // Save it on cache to not fetch again
                DataCacheManager.instance.addApplicationWithError(applicationId);
              } finally {
                // Application was fetched
                DataCacheManager.instance.removePendingApplication(applicationId);

                // Update context with applications
                if (updateContext) {
                  auxiliaryDataCacheContext.setApplications(DataCacheManager.instance.applications);
                }
              }
            } else {
              // Set it
              setApplication(DataCacheManager.instance.applications[applicationId]);
            }
          } else {
            // Log
            console.debug(
              "⌛ Application already being fetched. Let's wait for id:",
              applicationId
            );
          }
        } else {
          // Log
          console.debug(
            '🔴 Could not get the application because an error occurred previously with id:',
            applicationId
          );
        }
      } else {
        // Log
        console.debug('🔴 Could not get the application because the id is set to:', applicationId);
      }
    }
  };

  const setReminderOnActivityCard = async () => {
    if (props.value.activityType.dataType === ActivityTypeDataType.ReminderResponse && !reminder) {
      // Reminder id
      const reminderId: string | undefined = props.value.activity.data
        ? (props.value.activity.data as ReminderResponseActivityData).reminderId
        : undefined;

      // Make sure the reminder id is set
      if (reminderId) {
        // Make sure an error did not occur previously with the reminder
        if (!DataCacheManager.instance.remindersWithError[reminderId]) {
          if (!DataCacheManager.instance.pendingReminders[reminderId]) {
            // Load reminder into cache if needed
            if (!DataCacheManager.instance.reminders[reminderId]) {
              let updateContext: boolean = false;

              try {
                // Reminder is being fetched
                DataCacheManager.instance.addPendingReminder(reminderId);

                // Get
                const result: Reminder = await RemindersRepository.instance.getById(reminderId);

                if (result) {
                  // Save it on cache
                  DataCacheManager.instance.addReminder(reminderId, result);

                  // Update
                  updateContext = true;

                  // Set it
                  setReminder(result);
                } else {
                  // Error
                  throw new Error('Empty reminder.');
                }
              } catch (error) {
                // Log
                console.debug(`🔴 Could not get reminder ${reminderId}.`, error);

                // Save it on cache to not fetch again
                DataCacheManager.instance.addReminderWithError(reminderId);
              } finally {
                // Reminder was fetched
                DataCacheManager.instance.removePendingReminder(reminderId);

                // Update context with reminders
                if (updateContext) {
                  auxiliaryDataCacheContext.setReminders(DataCacheManager.instance.reminders);
                }
              }
            } else {
              // Set it
              setReminder(DataCacheManager.instance.reminders[reminderId]);
            }
          } else {
            // Log
            console.debug("⌛ Reminder already being fetched. Let's wait for id:", reminderId);
          }
        } else {
          // Log
          console.debug(
            '🔴 Could not get the reminder because an error occurred previously with id:',
            reminderId
          );
        }
      } else {
        // Log
        console.debug('🔴 Could not get the reminder because the id is set to:', reminderId);
      }
    }
  };

  const setParticipantsOnActivityCard = async () => {
    if (
      props.value.activityType.dataType === ActivityTypeDataType.ApplicationBattle ||
      props.value.activityType.dataType === ActivityTypeDataType.CallMissed ||
      props.value.activityType.dataType === ActivityTypeDataType.CallRejected ||
      props.value.activityType.dataType === ActivityTypeDataType.CallStarted ||
      props.value.activityType.dataType === ActivityTypeDataType.DrawingSent ||
      props.value.activityType.dataType === ActivityTypeDataType.DrawingSaved ||
      props.value.activityType.dataType === ActivityTypeDataType.DrawingShared ||
      props.value.activityType.dataType === ActivityTypeDataType.NewRelationship ||
      props.value.activityType.dataType === ActivityTypeDataType.PhotoSent ||
      props.value.activityType.dataType === ActivityTypeDataType.PhotoSaved ||
      props.value.activityType.dataType === ActivityTypeDataType.PhotoShared ||
      props.value.activityType.dataType === ActivityTypeDataType.VideoMessage ||
      props.value.activityType.dataType === ActivityTypeDataType.VideoSent ||
      props.value.activityType.dataType === ActivityTypeDataType.VideoSaved ||
      props.value.activityType.dataType === ActivityTypeDataType.VideoShared
    ) {
      // Init participantsUserIds
      let participantsUserIds: Array<string> = [];

      // Fill array
      if (
        props.value.activityType.dataType === ActivityTypeDataType.ApplicationBattle &&
        props.value.activity.data &&
        Array.isArray(
          (props.value.activity.data as ApplicationBattleActivityData).participantsUserIds
        )
      ) {
        participantsUserIds = [...props.value.activity.data.participantsUserIds];
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.CallMissed &&
        props.value.activity.data &&
        (props.value.activity.data as CallMissedActivityData).callerId
      ) {
        participantsUserIds.push(props.value.activity.data.callerId);
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.CallRejected &&
        props.value.activity.data &&
        (props.value.activity.data as CallRejectedActivityData).callerId
      ) {
        participantsUserIds.push(props.value.activity.data.callerId);
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.CallStarted &&
        props.value.activity.data
      ) {
        if ((props.value.activity.data as CallStartedActivityData).callerId) {
          participantsUserIds.push(props.value.activity.data.callerId);
        }
        if ((props.value.activity.data as CallStartedActivityData).calleeId) {
          participantsUserIds.push(props.value.activity.data.calleeId);
        }
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.DrawingSent &&
        props.value.activity.data &&
        Array.isArray((props.value.activity.data as DrawingSentActivityData).sentToUsersIds)
      ) {
        participantsUserIds = [...props.value.activity.data.sentToUsersIds];
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.DrawingSaved &&
        props.value.activity.data &&
        Array.isArray((props.value.activity.data as DrawingSavedActivityData).sentToUsersIds)
      ) {
        participantsUserIds = [...props.value.activity.data.sentToUsersIds];
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.DrawingShared &&
        props.value.activity.data &&
        (props.value.activity.data as DrawingSharedActivityData).seniorUserId !==
          props.value.activity.userId
      ) {
        participantsUserIds = [props.value.activity.data.seniorUserId];
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.NewRelationship &&
        props.value.activity.data &&
        (props.value.activity.data as NewRelationshipActivityData).withUserId
      ) {
        participantsUserIds.push(props.value.activity.data.withUserId);
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.PhotoSent &&
        props.value.activity.data
      ) {
        if ((props.value.activity.data as PhotoSentActivityData).sentToUserId) {
          // Old way
          participantsUserIds.push(props.value.activity.data.sentToUserId);
        } else if (
          Array.isArray((props.value.activity.data as PhotoSentActivityData).sentToUsersIds)
        ) {
          // New way
          participantsUserIds = [...props.value.activity.data.sentToUsersIds];
        }
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.PhotoSaved &&
        props.value.activity.data &&
        Array.isArray((props.value.activity.data as PhotoSavedActivityData).sentToUsersIds)
      ) {
        participantsUserIds = [...props.value.activity.data.sentToUsersIds];
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.PhotoShared &&
        props.value.activity.data &&
        (props.value.activity.data as PhotoSharedActivityData).seniorUserId !==
          props.value.activity.userId
      ) {
        participantsUserIds = [props.value.activity.data.seniorUserId];
      } else if (props.value.activityType.dataType === ActivityTypeDataType.VideoMessage) {
        // New way
        if (
          props.value.activity.participantsUserIds &&
          Array.isArray(props.value.activity.participantsUserIds)
        ) {
          participantsUserIds = [...props.value.activity.participantsUserIds];
        }

        // Support old "Received a video message" and "Sent a video message" activities
        if (
          props.value.activity.data &&
          (props.value.activity.data as VideoMessageActivityData).sentToUserId &&
          !participantsUserIds.find(
            (id) => id === (props.value.activity.data as VideoMessageActivityData).sentToUserId
          )
        ) {
          participantsUserIds.push(
            (props.value.activity.data as VideoMessageActivityData).sentToUserId as string
          );
        }
        if (
          props.value.activity.data &&
          (props.value.activity.data as VideoMessageActivityData).sentFromUserId &&
          !participantsUserIds.find(
            (id) => id === (props.value.activity.data as VideoMessageActivityData).sentFromUserId
          )
        ) {
          participantsUserIds.push(
            (props.value.activity.data as VideoMessageActivityData).sentFromUserId as string
          );
        }
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.VideoSent &&
        props.value.activity.data &&
        Array.isArray((props.value.activity.data as VideoSentActivityData).sentToUsersIds)
      ) {
        participantsUserIds = [...props.value.activity.data.sentToUsersIds];
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.VideoSaved &&
        props.value.activity.data &&
        Array.isArray((props.value.activity.data as VideoSavedActivityData).sentToUsersIds)
      ) {
        participantsUserIds = [...props.value.activity.data.sentToUsersIds];
      } else if (
        props.value.activityType.dataType === ActivityTypeDataType.VideoShared &&
        props.value.activity.data &&
        (props.value.activity.data as VideoSharedActivityData).seniorUserId !==
          props.value.activity.userId
      ) {
        participantsUserIds = [props.value.activity.data.seniorUserId];
      }

      if (participants.length !== participantsUserIds.length) {
        // Load participants
        let result: Array<UserPublicInfo> = [];
        for (const participantUserId of participantsUserIds) {
          // Make sure the participant id is set
          if (participantUserId) {
            // Make sure an error did not occur previously with the participant
            if (!DataCacheManager.instance.usersWithError[participantUserId]) {
              if (!DataCacheManager.instance.pendingUsers[participantUserId]) {
                // Load user into cache if needed
                if (!DataCacheManager.instance.users[participantUserId]) {
                  let updateContext: boolean = false;

                  try {
                    // User is being fetched
                    DataCacheManager.instance.addPendingUser(participantUserId);

                    // Fetch user info
                    const user: UserPublicInfo =
                      await UsersRepository.instance.getInfoById(participantUserId);

                    if (user) {
                      // Save it on cache
                      DataCacheManager.instance.addUser(participantUserId, user);

                      // Update
                      updateContext = true;

                      // Add to list
                      result.push(user);
                    } else {
                      // Error
                      throw new Error('Empty user.');
                    }
                  } catch (error) {
                    // Log
                    console.debug(`🔴 Could not get user ${participantUserId}.`, error);

                    // Save it on cache to not fetch again
                    DataCacheManager.instance.addUserWithError(participantUserId);
                  } finally {
                    // User was fetched
                    DataCacheManager.instance.removePendingUser(participantUserId);

                    // Update context with users
                    if (updateContext) {
                      auxiliaryDataCacheContext.setUsers(DataCacheManager.instance.users);
                    }
                  }
                } else {
                  // Add to list
                  result.push(DataCacheManager.instance.users[participantUserId]);
                }
              } else {
                // Log
                console.debug(
                  "⌛ Participant already being fetched. Let's wait for id:",
                  participantUserId
                );
              }
            } else {
              // Log
              console.debug(
                '🔴 Could not get the user because an error occurred previously with id:',
                participantUserId
              );
            }
          } else {
            // Log
            console.debug('🔴 Could not get the user because the id is set to:', participantUserId);
          }
        }

        // Set it
        setParticipants(result);
      }
    }
  };

  // Handle error on the image
  const handleErrorOnImage = () => {
    setErrorOnImage(true);
  };

  // Handle error on the video
  const handleErrorOnVideo = () => {
    setErrorOnVideo(true);
  };

  // Show/hide description
  const handleDescriptionVisibility = () => {
    setShowDescription((prevState) => !prevState);
  };

  // Today
  const today = moment().startOf('day');
  const happenedToday: boolean = moment(props.value.activity.createdAt).startOf('day').isSame(today)
    ? true
    : false;

  // Duration
  let duration: string | undefined;
  if (
    (props.value.activity.endType !== ActivityEndType.SystemTimeout &&
      props.caregiverMode !== CaregiverMode.SystemAdministrator) ||
    props.caregiverMode === CaregiverMode.SystemAdministrator
  ) {
    if (props.value.activity.durationInSeconds && props.value.activity.durationInSeconds < 60) {
      duration = `${Math.round(props.value.activity.durationInSeconds)}${t(translations.time_units.seconds)}`;
    } else if (
      props.value.activity.durationInMinutes &&
      props.value.activity.durationInMinutes < 60
    ) {
      duration = `${Math.round(props.value.activity.durationInMinutes)}${t(translations.time_units.minutes)}`;
    } else if (props.value.activity.durationInHours) {
      duration = hoursToHoursAndMinutes(props.value.activity.durationInHours as number);
    }

    if (
      props.caregiverMode === CaregiverMode.SystemAdministrator &&
      props.value.activity.endType === ActivityEndType.SystemTimeout
    ) {
      duration += ` (${t(translations.activity_end_types['system-timeout'])})`;
    }
  }

  // Media
  let showMedia:
    | {
        mediaType: 'video' | 'image';
        mediaUrl?: string;
      }
    | undefined;
  if (
    props.value.activityType.dataType === ActivityTypeDataType.DrawingSent &&
    props.value.activity.data &&
    (props.value.activity.data as DrawingSentActivityData).drawingUrl &&
    Array.isArray((props.value.activity.data as DrawingSentActivityData).sentToUsersIds) &&
    (props.value.activity.data as DrawingSentActivityData).sentToUsersIds.find(
      (id) => authContext.state.session?.id && id === authContext.state.session.id
    )
  ) {
    showMedia = {
      mediaUrl: (props.value.activity.data as DrawingSentActivityData).drawingUrl,
      mediaType: 'image',
    };
  } else if (
    props.value.activityType.dataType === ActivityTypeDataType.DrawingShared &&
    props.value.activity.data &&
    (props.value.activity.data as DrawingSharedActivityData).drawingUrl &&
    Array.isArray((props.value.activity.data as DrawingSharedActivityData).sharedWithUsersIds) &&
    (props.value.activity.userId === authContext.state.session?.id ||
      (props.value.activity.data as DrawingSharedActivityData).sharedWithUsersIds.find(
        (id) => authContext.state.session?.id && id === authContext.state.session.id
      ))
  ) {
    showMedia = {
      mediaUrl: (props.value.activity.data as DrawingSharedActivityData).drawingUrl,
      mediaType: 'image',
    };
  } else if (
    props.value.activityType.dataType === ActivityTypeDataType.DrawingSaved &&
    props.value.activity.data &&
    (props.value.activity.data as DrawingSavedActivityData).drawingUrl &&
    Array.isArray((props.value.activity.data as DrawingSavedActivityData).sentToUsersIds) &&
    ((props.value.activity.data as DrawingSavedActivityData).sentToUsersIds as Array<string>).find(
      (id) => authContext.state.session?.id && id === authContext.state.session.id
    )
  ) {
    showMedia = {
      mediaUrl: (props.value.activity.data as DrawingSavedActivityData).drawingUrl,
      mediaType: 'image',
    };
  } else if (
    props.value.activityType.dataType === ActivityTypeDataType.PhotoSent &&
    props.value.activity.data &&
    (props.value.activity.data as PhotoSentActivityData).mediaUrl &&
    authContext.state.session?.id &&
    ((props.value.activity.data as PhotoSentActivityData).sentToUserId ===
      authContext.state.session.id ||
      (props.value.activity.data as PhotoSentActivityData).sentToUsersIds.find(
        (id) => authContext.state.session?.id && id === authContext.state.session.id
      ))
  ) {
    showMedia = {
      mediaUrl: (props.value.activity.data as PhotoSentActivityData).mediaUrl,
      mediaType: 'image',
    };
  } else if (
    props.value.activityType.dataType === ActivityTypeDataType.PhotoShared &&
    props.value.activity.data &&
    (props.value.activity.data as PhotoSharedActivityData).mediaUrl &&
    authContext.state.session?.id &&
    (props.value.activity.userId === authContext.state.session?.id ||
      (props.value.activity.data as PhotoSharedActivityData).sharedWithUsersIds.find(
        (id) => authContext.state.session?.id && id === authContext.state.session.id
      ))
  ) {
    showMedia = {
      mediaUrl: (props.value.activity.data as PhotoSharedActivityData).mediaUrl,
      mediaType: 'image',
    };
  } else if (
    props.value.activityType.dataType === ActivityTypeDataType.PhotoSaved &&
    props.value.activity.data &&
    (props.value.activity.data as PhotoSavedActivityData).mediaUrl &&
    Array.isArray((props.value.activity.data as PhotoSavedActivityData).sentToUsersIds) &&
    ((props.value.activity.data as PhotoSavedActivityData).sentToUsersIds as Array<string>).find(
      (id) => authContext.state.session?.id && id === authContext.state.session.id
    )
  ) {
    showMedia = {
      mediaUrl: (props.value.activity.data as PhotoSavedActivityData).mediaUrl,
      mediaType: 'image',
    };
  } else if (
    props.value.activityType.dataType === ActivityTypeDataType.VideoMessage &&
    props.value.activity.data &&
    (props.value.activity.data as VideoMessageActivityData).mediaUrl &&
    authContext.state.session?.id &&
    ((props.value.activity.data as VideoMessageActivityData).sentToUserId ===
      authContext.state.session.id ||
      ((props.value.activity.data as VideoMessageActivityData).sentFromUserId &&
        props.value.user.id === authContext.state.session.id))
  ) {
    showMedia = {
      mediaUrl: (props.value.activity.data as VideoMessageActivityData).mediaUrl,
      mediaType: 'video',
    };
  } else if (
    props.value.activityType.dataType === ActivityTypeDataType.VideoSent &&
    props.value.activity.data &&
    (props.value.activity.data as VideoSentActivityData).mediaUrl &&
    Array.isArray((props.value.activity.data as VideoSentActivityData).sentToUsersIds) &&
    ((props.value.activity.data as VideoSentActivityData).sentToUsersIds as Array<string>).find(
      (id) => authContext.state.session?.id && id === authContext.state.session.id
    )
  ) {
    showMedia = {
      mediaUrl: (props.value.activity.data as VideoSentActivityData).mediaUrl,
      mediaType: 'video',
    };
  } else if (
    props.value.activityType.dataType === ActivityTypeDataType.VideoShared &&
    props.value.activity.data &&
    (props.value.activity.data as VideoSharedActivityData).mediaUrl &&
    Array.isArray((props.value.activity.data as VideoSharedActivityData).sharedWithUsersIds) &&
    (props.value.activity.userId === authContext.state.session?.id ||
      (props.value.activity.data as VideoSharedActivityData).sharedWithUsersIds.find(
        (id) => authContext.state.session?.id && id === authContext.state.session.id
      ))
  ) {
    showMedia = {
      mediaUrl: (props.value.activity.data as VideoSharedActivityData).mediaUrl,
      mediaType: 'video',
    };
  } else if (
    props.value.activityType.dataType === ActivityTypeDataType.VideoSaved &&
    props.value.activity.data &&
    (props.value.activity.data as VideoSavedActivityData).mediaUrl &&
    Array.isArray((props.value.activity.data as VideoSavedActivityData).sentToUsersIds) &&
    ((props.value.activity.data as VideoSavedActivityData).sentToUsersIds as Array<string>).find(
      (id) => authContext.state.session?.id && id === authContext.state.session.id
    )
  ) {
    showMedia = {
      mediaUrl: (props.value.activity.data as VideoSavedActivityData).mediaUrl,
      mediaType: 'video',
    };
  }

  return (
    <TimelineItem
      className="activity-card"
      sx={{
        // Remove unnecessary left content
        '&::before': {
          content: 'none',
        },
      }}
    >
      <TimelineSeparator>
        <TimelineConnector sx={{bgcolor: 'primary.main'}} />
        <Tooltip title={props.value.activityType.name}>
          <TimelineDot
            className="activity-card-dot-container"
            sx={{
              bgcolor: props.value.applicationCategory
                ? props.value.applicationCategory.systemButton.enabledStateColor
                : theme.palette.primary.main,
              width: `${avatarSizesAndCoefficients.avatarSizes.large}px`,
              height: `${avatarSizesAndCoefficients.avatarSizes.large}px`,
              borderRadius: `${
                avatarSizesAndCoefficients.avatarSizes.large *
                avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersMedium
              }px`,
            }}
          >
            {isSvg(props.value.activityType.iconUrl) ? (
              <object
                data={props.value.activityType.iconUrl}
                aria-label={props.value.activityType.name}
                type="image/svg+xml"
                width={`${
                  avatarSizesAndCoefficients.avatarSizes.large *
                  avatarSizesAndCoefficients.iconSizeCoefficient
                }`}
                height={`${
                  avatarSizesAndCoefficients.avatarSizes.large *
                  avatarSizesAndCoefficients.iconSizeCoefficient
                }`}
              ></object>
            ) : (
              <img
                style={{
                  width: `${
                    avatarSizesAndCoefficients.avatarSizes.large *
                    avatarSizesAndCoefficients.iconSizeCoefficient
                  }px`,
                  height: `${
                    avatarSizesAndCoefficients.avatarSizes.large *
                    avatarSizesAndCoefficients.iconSizeCoefficient
                  }px`,
                }}
                alt={props.value.activityType.name}
                src={props.value.activityType.iconUrl}
              />
            )}
          </TimelineDot>
        </Tooltip>
        <TimelineConnector sx={{bgcolor: 'primary.main'}} />
      </TimelineSeparator>
      <TimelineContent className="activity-card-content-container">
        <Box className="activity-card-content-user-and-time-container">
          <Box className="activity-card-content-user-container">
            <Avatar
              alt={props.value.user.name}
              src={props.value.user.picture}
              sx={{
                width: `${avatarSizesAndCoefficients.avatarSizes.small}px`,
                height: `${avatarSizesAndCoefficients.avatarSizes.small}px`,
              }}
            >
              <PersonIcon
                sx={{
                  width: `${
                    avatarSizesAndCoefficients.avatarSizes.small *
                    avatarSizesAndCoefficients.iconSizeCoefficient
                  }px`,
                  height: `${
                    avatarSizesAndCoefficients.avatarSizes.small *
                    avatarSizesAndCoefficients.iconSizeCoefficient
                  }px`,
                }}
              />
            </Avatar>
            <Typography
              variant="body2"
              component="span"
              className="activity-card-content-user-name"
            >
              {props.value.user.name}
            </Typography>
          </Box>
          <Tooltip
            title={moment(props.value.activity.createdAt).format('LTS')}
            placement="bottom-end"
          >
            <Typography align="left" variant="body2" color="text.secondary">
              {`${moment(props.value.activity.createdAt).format('LT')}${
                happenedToday ? ` (${moment(props.value.activity.createdAt).fromNow()})` : ''
              }`}
            </Typography>
          </Tooltip>
        </Box>
        <Box className="activity-card-content-title-container">
          <Typography variant="h6" component="span">
            {props.value.activity.endedAt
              ? props.value.activityType.pastTimeLabel
              : props.value.activityType.presentTimeLabel}
          </Typography>
          <IconButton onClick={handleDescriptionVisibility}>
            <InfoIcon sx={{color: 'text.secondary'}} />
          </IconButton>
        </Box>
        {showDescription && (
          <Box className="activity-card-content-description-container">
            <Typography variant="body2">{props.value.activityType.description}</Typography>
          </Box>
        )}
        {props.value.activityType.dataType === ActivityTypeDataType.ApplicationBattle &&
          application &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <ApplicationChip value={application} />
              <Typography className="activity-card-center-text" variant="body2">
                {t(translations.activity_card.application_battle_with)}
              </Typography>
              <ParticipantsChips activityId={props.value.activity.id} value={participants} />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.ApplicationProgress &&
          application && (
            <Box className="activity-card-row-container">
              <Typography className="activity-card-left-text" variant="body2">
                {t(translations.activity_card.level_on_app, {
                  level: props.value.activity.data.level,
                })}
              </Typography>
              <ApplicationChip value={application} />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.CallMissed &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <UserChip value={participants[0]} />
              <Typography className="activity-card-right-text" variant="body2">
                {t(translations.activity_card.missed_call) as string}
              </Typography>
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.CallRejected &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <UserChip value={participants[0]} />
              <Typography className="activity-card-right-text" variant="body2">
                {t(translations.activity_card.rejected_call) as string}
              </Typography>
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.CallStarted &&
          props.value.activity.data &&
          participants.find(
            (obj) => obj.id === (props.value.activity.data as CallStartedActivityData).callerId
          ) &&
          participants.find(
            (obj) => obj.id === (props.value.activity.data as CallStartedActivityData).calleeId
          ) && (
            <Box className="activity-card-row-container">
              <UserChip
                value={
                  participants.find(
                    (obj) =>
                      obj.id === (props.value.activity.data as CallStartedActivityData).callerId
                  ) as UserPublicInfo
                }
              />
              <Typography className="activity-card-center-text" variant="body2">
                {t(translations.activity_card.call_started) as string}
              </Typography>
              <UserChip
                value={
                  participants.find(
                    (obj) =>
                      obj.id === (props.value.activity.data as CallStartedActivityData).calleeId
                  ) as UserPublicInfo
                }
              />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.DrawingSent &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <Typography className="activity-card-left-text" variant="body2">
                {t(translations.activity_card.drawing_sent) as string}
              </Typography>
              <ParticipantsChips activityId={props.value.activity.id} value={participants} />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.DrawingSaved &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <Typography className="activity-card-left-text" variant="body2">
                {t(translations.activity_card.drawing_saved) as string}
              </Typography>
              <ParticipantsChips activityId={props.value.activity.id} value={participants} />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.DrawingShared &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <Typography className="activity-card-left-text" variant="body2">
                {t(translations.activity_card.drawing_shared) as string}
              </Typography>
              <ParticipantsChips activityId={props.value.activity.id} value={participants} />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.NewRelationship &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <UserChip value={participants[0]} />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.PhotoSent &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <Typography className="activity-card-left-text" variant="body2">
                {t(translations.activity_card.photo_sent) as string}
              </Typography>
              <ParticipantsChips
                activityId={props.value.activity.id}
                // Don't show that the user sent the photo to itself
                value={[...participants].filter((obj) => obj.id !== props.value.user.id)}
              />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.PhotoSaved &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <Typography className="activity-card-left-text" variant="body2">
                {t(translations.activity_card.photo_saved) as string}
              </Typography>
              <ParticipantsChips activityId={props.value.activity.id} value={participants} />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.PhotoShared &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <Typography className="activity-card-left-text" variant="body2">
                {t(translations.activity_card.photo_shared) as string}
              </Typography>
              <ParticipantsChips activityId={props.value.activity.id} value={participants} />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.PhotoTaken && (
          <Box className="activity-card-row-container">
            <Typography className="activity-card-left-text" variant="body2">
              {t(translations.activity_card.camera_used) as string}
            </Typography>
            <CameraFacingChip value={props.value.activity.data.cameraFacing} />
          </Box>
        )}
        {props.value.activityType.dataType === ActivityTypeDataType.ReminderResponse &&
          reminder && (
            <Box className="activity-card-column-container">
              <ReminderChip value={reminder} />
              <Box className="activity-card-reminder-response-container">
                <ReminderResponseValueChip value={props.value.activity.data.value} />
              </Box>
            </Box>
          )}
        {/* Support old "Received a video message" activity */}
        {props.value.activityType.dataType === ActivityTypeDataType.VideoMessage &&
          (props.value.activity.data as VideoMessageActivityData).sentFromUserId &&
          !(props.value.activity.data as VideoMessageActivityData).sentToUserId &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <Typography className="activity-card-left-text" variant="body2">
                {t(translations.activity_card.video_message_sent_by) as string}
              </Typography>
              <ParticipantsChips
                activityId={props.value.activity.id}
                value={[...participants].filter(
                  (obj) =>
                    obj.id ===
                    (props.value.activity.data as VideoMessageActivityData).sentFromUserId
                )}
              />
            </Box>
          )}
        {/* "Sent a video message" activity */}
        {props.value.activityType.dataType === ActivityTypeDataType.VideoMessage &&
          (props.value.activity.data as VideoMessageActivityData).sentToUserId &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <Typography className="activity-card-left-text" variant="body2">
                {t(translations.activity_card.video_message_sent_to) as string}
              </Typography>
              <ParticipantsChips
                activityId={props.value.activity.id}
                value={[...participants].filter(
                  (obj) =>
                    obj.id === (props.value.activity.data as VideoMessageActivityData).sentToUserId
                )}
              />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.VideoSent &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <Typography className="activity-card-left-text" variant="body2">
                {t(translations.activity_card.video_sent) as string}
              </Typography>
              <ParticipantsChips
                activityId={props.value.activity.id}
                // Don't show that the user sent the video to itself
                value={[...participants].filter((obj) => obj.id !== props.value.user.id)}
              />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.VideoSaved &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <Typography className="activity-card-left-text" variant="body2">
                {t(translations.activity_card.video_saved) as string}
              </Typography>
              <ParticipantsChips activityId={props.value.activity.id} value={participants} />
            </Box>
          )}
        {props.value.activityType.dataType === ActivityTypeDataType.VideoShared &&
          participants.length > 0 && (
            <Box className="activity-card-row-container">
              <Typography className="activity-card-left-text" variant="body2">
                {t(translations.activity_card.video_shared) as string}
              </Typography>
              <ParticipantsChips activityId={props.value.activity.id} value={participants} />
            </Box>
          )}
        {(props.value.device || duration || (!duration && !props.value.activity.endedAt)) && (
          <Box className="activity-card-content-device-duration-container">
            {props.value.device && (
              <Tooltip title={t(translations.activity_card.device) as string}>
                <Box className="activity-card-content-device-container">
                  {props.value.device.type === DeviceType.Desktop && (
                    <DesktopIcon
                      fontSize="small"
                      className="activity-card-content-device-container-icon"
                      sx={{color: 'text.secondary'}}
                    />
                  )}
                  {props.value.device.type === DeviceType.Tablet && (
                    <TabletIcon
                      fontSize="small"
                      className="activity-card-content-device-container-icon"
                      sx={{color: 'text.secondary'}}
                    />
                  )}
                  <Typography variant="body2">
                    {t(translations.device_types[props.value.device.type])}
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {duration && (
              <Tooltip title={t(translations.activity_card.duration) as string}>
                <Box className="activity-card-content-duration-container">
                  <HourglassEmptyIcon
                    className="activity-card-content-duration-container-icon"
                    fontSize="small"
                    sx={{color: 'text.secondary'}}
                  />
                  <Typography variant="body2">{duration}</Typography>
                </Box>
              </Tooltip>
            )}
            {!duration && !props.value.activity.endedAt && (
              <Tooltip title={t(translations.activity_card.duration) as string}>
                <Box className="activity-card-content-duration-container">
                  <HourglassEmptyIcon
                    className="activity-card-content-duration-container-icon"
                    fontSize="small"
                    sx={{color: theme.palette.primary.main}}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    {t(translations.activity_card.currently_happening)}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        )}
        {showMedia && !errorOnImage && !errorOnVideo && (
          <Box className="activity-card-media-container">
            {showMedia.mediaType === 'image' && (
              <img
                className="activity-card-media"
                src={showMedia.mediaUrl}
                onError={handleErrorOnImage}
                alt=""
              />
            )}
            {showMedia.mediaType === 'video' && (
              <video className="activity-card-media" onError={handleErrorOnVideo} controls>
                <source src={showMedia.mediaUrl} />
              </video>
            )}
          </Box>
        )}
      </TimelineContent>
    </TimelineItem>
  );
};
